import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GlassCard, GlassButton, GlassBadge } from '../../components/ui/GlassComponents';
import { countWorkingDays } from '../../utils/leaveDayCount';
import SubscriptionStatus from '../../components/subscription/SubscriptionStatus';
import TeamLicenseUsageSummary from '../../components/team/TeamLicenseUsageSummary';
import OnboardingWelcome from '../../components/onboarding/OnboardingWelcome';
import SubscriptionWarning from '../../components/common/SubscriptionWarning';
import FeatureBlockedModal from '../../components/common/FeatureBlockedModal';

// Calendar components
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

// Import calendar styles
import './Calendar.css';

const Dashboard = () => {
  const { user, team } = useAuth();
  const [loading, setLoading] = useState(true);
  const [whatsOn, setWhatsOn] = useState({
    offToday: [],
    offThisWeek: [],
    nextAbsence: null,
    pendingApprovalsCount: null
  });
  const [stats, setStats] = useState({
    pendingRequests: 0,
    upcomingLeaves: 0,
    teamOnLeaveToday: 0,
    availableDays: 0
  });
  
  // Team member statistics for subscription limit warning
  const [teamStats, setTeamStats] = useState({
    activeMembers: 0,
    pendingInvitations: 0,
    planUserLimit: team?.planUserLimit || 5
  });
  const [recentLeaves, setRecentLeaves] = useState([]);
  const [events, setEvents] = useState([]);
  const [bankHolidayEvents, setBankHolidayEvents] = useState([]);
  const [availableLeaveTypes, setAvailableLeaveTypes] = useState([]);
  const [workingDays, setWorkingDays] = useState(null); // util defaults to Mon-Fri
  
  // State for date selection and request modal
  const [dashboardData, setDashboardData] = useState({
    stats: {},
    recentLeaves: [],
    calendarEvents: []
  });
  const [leaveBalances, setLeaveBalances] = useState({});
  const [selectedDates, setSelectedDates] = useState({
    start: null,
    end: null
  });
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [leaveRequest, setLeaveRequest] = useState({
    type: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Onboarding popup state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  
  // Subscription warning state
  const [showFeatureBlockedModal, setShowFeatureBlockedModal] = useState(false);
  
  // Check if user needs onboarding on component mount
  useEffect(() => {
    if (user && (user.role === 'team_admin' || user.role === 'super_admin') && !user.onboardingCompleted && !onboardingDismissed) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(false);
    }
  }, [user, onboardingDismissed]);
  
  // Handle onboarding popup close
  const handleOnboardingClose = () => {
    setShowOnboarding(false);
    setOnboardingDismissed(true);
  };

  const formatShortDate = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString();
  };

  // Initials avatar helpers for the What's happening panels.
  // "John Smith" → "JS"; single names yield a single letter; empty → "?".
  const initialsFromName = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Stable gradient-per-person — initials sit on a gradient circle in white,
  // matching the gradient-heavy LeaveWise theme. Six options so a team of
  // ~20 still has visual variety, and the same name always gets the same
  // gradient across reloads.
  const AVATAR_GRADIENTS = [
    'bg-gradient-to-br from-blue-400 to-purple-500',
    'bg-gradient-to-br from-purple-400 to-pink-500',
    'bg-gradient-to-br from-emerald-400 to-teal-500',
    'bg-gradient-to-br from-amber-400 to-orange-500',
    'bg-gradient-to-br from-pink-400 to-rose-500',
    'bg-gradient-to-br from-cyan-400 to-blue-500'
  ];
  const gradientForName = (name) => {
    let hash = 0;
    const s = name || '';
    for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
    return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
  };

  // Friendly relative-day label for the "Next upcoming" panel. Hides the
  // ISO-style date when something more human-readable will do.
  const relativeStart = (dateString) => {
    if (!dateString) return null;
    const start = new Date(dateString);
    if (Number.isNaN(start.getTime())) return null;
    const today = new Date();
    const a = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const b = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const diff = Math.round((b - a) / 86400000);
    if (diff === 0) return 'starts today';
    if (diff === 1) return 'starts tomorrow';
    if (diff > 1 && diff <= 14) return `in ${diff} days`;
    return null;
  };
  
  // Function to fetch dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch dashboard stats
      const statsResponse = await axios.get('/api/leaves/stats');

      const whatsOnResponse = await axios.get('/api/leaves/whats-on');
      
      // Fetch recent leaves
      const recentResponse = await axios.get('/api/leaves/recent');
      
      // Fetch calendar events
      const calendarResponse = await axios.get('/api/leaves/calendar');
      
      // Fetch leave balances
      const balancesResponse = await axios.get('/api/leaves/balances');

      // Pick up team workingDays so calculateBusinessDays matches server math
      try {
        const teamResponse = await axios.get('/api/teams/current');
        if (teamResponse.data && Array.isArray(teamResponse.data.workingDays)) {
          setWorkingDays(teamResponse.data.workingDays);
        }
      } catch (e) { /* soft failure — util defaults to Mon-Fri */ }

      // Fetch available leave types
      const leaveTypesResponse = await axios.get('/api/leaves/types');

      // Fetch UK bank holidays (info-only)
      let bankHolidaysResponse;
      try {
        bankHolidaysResponse = await axios.get('/api/bank-holidays');
      } catch (bhError) {
        console.error('Error fetching bank holidays:', bhError);
        bankHolidaysResponse = null;
      }
      
      // Fetch team member statistics if user is a team admin
      if (user.role === 'team_admin') {
        try {
          const teamMembersResponse = await axios.get('/api/teams/members');
          
          if (Array.isArray(teamMembersResponse.data)) {
            const activeMembers = teamMembersResponse.data.filter(member => 
              member.status === 'active' && !member.isPendingInvitation
            ).length;
            
            const pendingInvitations = teamMembersResponse.data.filter(member => 
              member.isPendingInvitation
            ).length;
            
            setTeamStats({
              activeMembers,
              pendingInvitations,
              planUserLimit: team?.planUserLimit || 5
            });
          }
        } catch (error) {
          console.error('Error fetching team member statistics:', error);
        }
      }
      
      console.log('Stats data received:', statsResponse.data);
      console.log('Recent leaves received:', recentResponse.data);
      console.log('Calendar data received:', calendarResponse.data);
      console.log('Available leave types:', leaveTypesResponse.data);
      console.log('Leave balances received:', balancesResponse.data);
      
      // Make sure we have valid data before setting state
      if (statsResponse.data) {
        setStats(statsResponse.data);
      }

      if (whatsOnResponse.data?.success) {
        setWhatsOn({
          offToday: Array.isArray(whatsOnResponse.data.offToday) ? whatsOnResponse.data.offToday : [],
          offThisWeek: Array.isArray(whatsOnResponse.data.offThisWeek) ? whatsOnResponse.data.offThisWeek : [],
          nextAbsence: whatsOnResponse.data.nextAbsence || null,
          pendingApprovalsCount: typeof whatsOnResponse.data.pendingApprovalsCount === 'number'
            ? whatsOnResponse.data.pendingApprovalsCount
            : null
        });
      }
      
      if (Array.isArray(recentResponse.data)) {
        setRecentLeaves(recentResponse.data);
      }
      
      // Set leave balances
      if (balancesResponse.data) {
        setLeaveBalances(balancesResponse.data);
      }
      
      // Set available leave types
      if (Array.isArray(leaveTypesResponse.data)) {
        setAvailableLeaveTypes(leaveTypesResponse.data);
        
        // Set default leave request type if available
        if (leaveTypesResponse.data.length > 0 && !leaveRequest.type) {
          setLeaveRequest(prev => ({
            ...prev,
            type: leaveTypesResponse.data[0].id
          }));
        }
      }
      
      // Transform leave data to calendar events
      if (Array.isArray(calendarResponse.data)) {
        const calendarEvents = calendarResponse.data.map(leave => {
          // Find the leave type in available types to ensure we have color info
          const leaveTypeId = leave.customLeaveTypeId;
          const leaveTypeInfo = leaveTypesResponse.data.find(lt => lt.id === leaveTypeId);
          
          // Handle different user data formats
          const user = leave.user || leave.User;
          const userName = user && user.firstName 
            ? `${user.firstName} ${user.lastName || ''}` 
            : 'Unknown User';
          
          // Use the correct date properties and provide fallbacks
          const startDateStr = leave.start || leave.startDate;
          const endDateStr = leave.end || leave.endDate;
          
          if (!startDateStr || !endDateStr) {
            console.error(`Missing dates for event ${leave.id}:`, leave);
            return null;
          }
          
          // Determine display name for leave type
          let displayTypeName;
          if (leave.typeName) {
            // Use the typeName from backend if available (for custom types)
            displayTypeName = leave.typeName;
          } else if (leaveTypeInfo && leaveTypeInfo.name) {
            // If we found the leave type in available types, use its name
            displayTypeName = leaveTypeInfo.name;
          } else if (leave.type) {
            // Otherwise capitalize the standard type
            displayTypeName = leave.type.charAt(0).toUpperCase() + leave.type.slice(1);
          } else {
            displayTypeName = 'Leave';
          }
          
          // Get the color from the leave type info or from the leave itself
          const typeColor = (leaveTypeInfo && leaveTypeInfo.color) || leave.typeColor || '#6B7280';
          
          // Determine background color based on status and type
          const backgroundColor = leave.status === 'pending' ? '#FFA500' : typeColor;
          
          // Create a calendar event object
          // Ensure dates are properly formatted for FullCalendar
          const startDate = new Date(startDateStr);
          const endDate = new Date(endDateStr);
          
          // For all-day events, FullCalendar expects the end date to be exclusive
          // So we need to add one day to the end date
          const adjustedEndDate = new Date(endDate);
          adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
          
          return {
            id: leave.id,
            title: `${userName} - ${displayTypeName}`,
            start: startDate.toISOString(),
            end: adjustedEndDate.toISOString(),
            allDay: true,
            backgroundColor: backgroundColor,
            borderColor: backgroundColor,
            classNames: [`status-${leave.status}`],
            extendedProps: {
              userId: leave.userId,
              type: leave.type,
              status: leave.status,
              notes: leave.notes,
              userName,
              typeName: displayTypeName,
              typeColor: typeColor, // Store the determined color
              customLeaveTypeId: leave.customLeaveTypeId
            }
          };
        });
        
        setEvents(calendarEvents);
      }

      // Transform bank holidays into FullCalendar events
      const bankHolidays = bankHolidaysResponse?.data?.success ? bankHolidaysResponse.data.events : [];
      const transformedBankHolidays = (bankHolidays || []).map((bh) => {
        const startDate = new Date(bh.date);
        const endDate = new Date(bh.date);
        endDate.setDate(endDate.getDate() + 1);

        return {
          id: `bank-holiday-${bh.date}-${bh.title}`,
          title: `Bank Holiday - ${bh.title}`,
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          allDay: true,
          backgroundColor: '#2563EB',
          borderColor: '#2563EB',
          textColor: '#ffffff',
          classNames: ['bank-holiday-event'],
          extendedProps: {
            isBankHoliday: true,
            bankHoliday: bh
          }
        };
      });

      setBankHolidayEvents(transformedBankHolidays);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  // Track if the component is mounted
  const isMounted = useRef(true);
  
  // Call fetchDashboardData when component mounts or becomes visible
  useEffect(() => {
    // Fetch data when component mounts
    fetchDashboardData();
    
    // Set up event listener for when the page becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMounted.current) {
        fetchDashboardData();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Clean up
    return () => {
      isMounted.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  // Get color for leave type
  const getLeaveTypeColor = (leave) => {
    // Handle pending status with orange color
    if (leave && leave.status === 'pending') {
      return '#FFA500'; // Orange for pending
    }
    
    // If we have a typeColor from the backend, use it directly
    if (leave && leave.typeColor && typeof leave === 'object') {
      return leave.typeColor;
    }
    
    // Try to find the leave type in availableLeaveTypes
    const typeId = leave && typeof leave === 'object' ? 
      (leave.customLeaveTypeId || leave.extendedProps?.customLeaveTypeId) : leave;
    
    // Look up the color in availableLeaveTypes
    const leaveType = availableLeaveTypes.find(lt => lt.id === typeId);
    if (leaveType && leaveType.color) {
      return leaveType.color;
    }
    
    // If we have extendedProps with typeColor, use that
    if (leave && leave.extendedProps && leave.extendedProps.typeColor) {
      return leave.extendedProps.typeColor;
    }
    
    // Fallback color if type not found
    return '#6B7280'; // gray-600
  };
  
  // Get CSS class for status badge
  const getStatusBadgeClass = (status) => {
    const classes = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'canceled': 'bg-gray-100 text-gray-800'
    };
    
    return classes[status.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };
  
  // Handle calendar event click
  const handleEventClick = (info) => {
    const event = info.event;
    const props = event.extendedProps;

    if (props?.isBankHoliday) {
      toast(
        <div>
          <p className="font-semibold">{event.title}</p>
          <p>Date: {event.start.toLocaleDateString()}</p>
          {props.bankHoliday?.notes && <p>Notes: {props.bankHoliday.notes}</p>}
        </div>,
        { duration: 5000 }
      );
      return;
    }
    
    toast(
      <div>
        <p className="font-semibold">{event.title}</p>
        <p>Status: <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeClass(props.status)}`}>{props.status}</span></p>
        {props.notes && <p>Notes: {props.notes}</p>}
        <p>From: {event.start.toLocaleDateString()}</p>
        <p>To: {event.end.toLocaleDateString()}</p>
      </div>,
      { duration: 5000 }
    );
  };
  
  // Handle date selection for new leave requests
  const handleDateSelect = (selectInfo) => {
    // Check if user is in grace period
    if (user?.subscriptionExpired) {
      setShowFeatureBlockedModal(true);
      return;
    }
    
    setSelectedDates({
      start: selectInfo.start,
      end: selectInfo.end
    });
    setShowRequestModal(true);
  };

  const handleRenewSubscription = () => {
    window.location.href = '/subscription';
  };

  
  // Handle leave request form changes
  const handleLeaveRequestChange = (e) => {
    const { name, value } = e.target;
    setLeaveRequest(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Working-day count, respecting team.workingDays. Falls back to Mon-Fri
  // until the team config has been fetched.
  const calculateBusinessDays = (startDate, endDate) => {
    return countWorkingDays({ startDate, endDate, workingDays });
  };
  
  // Handle leave request submission
  const handleLeaveRequestSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDates.start || !selectedDates.end || !leaveRequest.type) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Prevent double submission
    if (isSubmitting) {
      return;
    }
    
    // Calculate requested days
    const requestedDays = calculateBusinessDays(selectedDates.start, selectedDates.end);
    
    // Check if user has enough leave balance
    const balance = leaveBalances[leaveRequest.type] || {};
    const availableDays = balance.remaining || 0;
    
    // Find the leave type name for better error messages
    const selectedLeaveType = availableLeaveTypes.find(type => type.id === leaveRequest.type);
    const typeName = selectedLeaveType ? selectedLeaveType.name : leaveRequest.type;
    
    if (requestedDays > availableDays) {
      toast.error(`Insufficient ${typeName} balance. You have ${availableDays} days available but are requesting ${requestedDays} days.`);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Format the request data to match the Calendar component
      const requestData = {
        startDate: selectedDates.start instanceof Date ? selectedDates.start.toISOString() : selectedDates.start,
        endDate: selectedDates.end instanceof Date ? selectedDates.end.toISOString() : selectedDates.end,
        leaveType: leaveRequest.type,
        reason: leaveRequest.notes
      };
      
      // Use the same endpoint as the Calendar component
      const response = await axios.post('/api/leaves/request', requestData);
      
      toast.success('Leave request submitted successfully');
      setShowRequestModal(false);
      fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error submitting leave request:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit leave request';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Subscription Warning Banner */}
      <SubscriptionWarning 
        user={user} 
        onRenewClick={handleRenewSubscription}
      />
      
      {/* Dashboard content starts here */}
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Dashboard</h1>
        <div className="mt-3 sm:mt-0">
          <Link to="/dashboard/request-leave" className="btn btn-primary">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            New Leave Request
          </Link>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <svg className="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      ) : (
        <>
          <GlassCard className="p-5 mb-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  What’s happening
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Team availability at a glance
                </p>
              </div>
              {/* Badge shows for anyone whose backend-computed approvals
                  count is a number — admins see team-wide, managers see
                  their department subset. Non-approvers get null. */}
              {typeof whatsOn.pendingApprovalsCount === 'number' && (
                <div className="text-right">
                  <div className="text-sm text-gray-600 dark:text-gray-300">Pending approvals</div>
                  <div className="text-2xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                    {whatsOn.pendingApprovalsCount}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">

              {/* Off today — amber/orange accent (warm, urgent) */}
              <div className="group relative overflow-hidden rounded-xl bg-white/60 dark:bg-gray-800/40 backdrop-blur-md ring-1 ring-white/60 dark:ring-white/10 shadow-sm hover:shadow-glow hover:scale-[1.01] transition-all duration-200 animate-fade-in">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
                <div className="p-4 pt-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700/80 dark:text-amber-300/80">Today</div>
                        <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 -mt-0.5">Out of office</div>
                      </div>
                    </div>
                    <div className="text-2xl font-bold bg-gradient-to-br from-amber-500 to-orange-600 bg-clip-text text-transparent tabular-nums">
                      {whatsOn.offToday.length}
                    </div>
                  </div>
                  {whatsOn.offToday.length === 0 ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="text-base">✨</span>
                      <span className="italic">Everyone's in today</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {whatsOn.offToday.slice(0, 4).map(item => (
                        <div key={item.id} className="flex items-center gap-2.5 min-w-0">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold text-white shadow-sm ring-2 ring-white dark:ring-gray-800 ${gradientForName(item.userName)}`}>
                            {initialsFromName(item.userName)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.userName}</div>
                            <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">until {formatShortDate(item.endDate)}</div>
                          </div>
                        </div>
                      ))}
                      {whatsOn.offToday.length > 4 && (
                        <div className="text-[11px] font-medium text-amber-700 dark:text-amber-300 pl-10">+{whatsOn.offToday.length - 4} more</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Off this week — blue/indigo accent (calm, scheduled) */}
              <div className="group relative overflow-hidden rounded-xl bg-white/60 dark:bg-gray-800/40 backdrop-blur-md ring-1 ring-white/60 dark:ring-white/10 shadow-sm hover:shadow-glow hover:scale-[1.01] transition-all duration-200 animate-fade-in">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-400 to-indigo-500" />
                <div className="p-4 pt-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-blue-700/80 dark:text-blue-300/80">This week</div>
                        <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 -mt-0.5">Booked off</div>
                      </div>
                    </div>
                    <div className="text-2xl font-bold bg-gradient-to-br from-blue-500 to-indigo-600 bg-clip-text text-transparent tabular-nums">
                      {whatsOn.offThisWeek.length}
                    </div>
                  </div>
                  {whatsOn.offThisWeek.length === 0 ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="text-base">📋</span>
                      <span className="italic">Quiet week ahead</span>
                    </div>
                  ) : whatsOn.offThisWeek.length > 4 ? (
                    // 5+ people: show face-pile + roll-up so it doesn't get tall.
                    <>
                      <div className="flex items-center mb-3">
                        {whatsOn.offThisWeek.slice(0, 6).map((item, idx) => (
                          <div
                            key={item.id}
                            className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold text-white shadow-sm ring-2 ring-white dark:ring-gray-800 ${gradientForName(item.userName)}`}
                            style={{ marginLeft: idx === 0 ? 0 : '-0.5rem', zIndex: 10 - idx }}
                            title={item.userName}
                          >
                            {initialsFromName(item.userName)}
                          </div>
                        ))}
                        {whatsOn.offThisWeek.length > 6 && (
                          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-semibold ring-2 ring-white dark:ring-gray-800 bg-gradient-to-br from-gray-400 to-gray-500 text-white shadow-sm" style={{ marginLeft: '-0.5rem' }}>
                            +{whatsOn.offThisWeek.length - 6}
                          </div>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400">
                        Hover an avatar to see who.
                      </div>
                    </>
                  ) : (
                    // 1–4 people: same inline pattern as the Today card —
                    // each name sits next to its own avatar.
                    <div className="space-y-2">
                      {whatsOn.offThisWeek.map(item => (
                        <div key={item.id} className="flex items-center gap-2.5 min-w-0">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold text-white shadow-sm ring-2 ring-white dark:ring-gray-800 ${gradientForName(item.userName)}`}>
                            {initialsFromName(item.userName)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.userName}</div>
                            <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{formatShortDate(item.startDate)}–{formatShortDate(item.endDate)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Next upcoming — violet/fuchsia accent (anticipation) */}
              <div className="group relative overflow-hidden rounded-xl bg-white/60 dark:bg-gray-800/40 backdrop-blur-md ring-1 ring-white/60 dark:ring-white/10 shadow-sm hover:shadow-glow hover:scale-[1.01] transition-all duration-200 animate-fade-in">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-400 to-fuchsia-500" />
                <div className="p-4 pt-5">
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-violet-400 to-fuchsia-500 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-700/80 dark:text-violet-300/80">Coming up</div>
                      <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 -mt-0.5">Next absence</div>
                    </div>
                  </div>
                  {!whatsOn.nextAbsence ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="text-base">🌤️</span>
                      <span className="italic">No upcoming absences</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-base font-semibold text-white shadow-md ring-2 ring-white dark:ring-gray-800 ${gradientForName(whatsOn.nextAbsence.userName)}`}>
                        {initialsFromName(whatsOn.nextAbsence.userName)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {whatsOn.nextAbsence.userName}
                        </div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                          {formatShortDate(whatsOn.nextAbsence.startDate)}–{formatShortDate(whatsOn.nextAbsence.endDate)}
                        </div>
                        {relativeStart(whatsOn.nextAbsence.startDate) && (
                          <div className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gradient-to-r from-violet-100 to-fuchsia-100 text-violet-700 dark:from-violet-900/40 dark:to-fuchsia-900/40 dark:text-violet-200">
                            {relativeStart(whatsOn.nextAbsence.startDate)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </GlassCard>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
            <GlassCard className="p-5 hover:shadow-glow hover:scale-[1.02] animate-fade-in">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg p-3 shadow-lg">
                  <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-600 dark:text-gray-300 truncate">Available Days</dt>
                    <dd>
                      <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{stats.availableDays}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </GlassCard>
            
            <GlassCard className="p-5 hover:shadow-glow hover:scale-[1.02] animate-fade-in">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg p-3 shadow-lg">
                  <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-600 dark:text-gray-300 truncate">Pending Requests</dt>
                    <dd>
                      <div className="text-2xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">{stats.pendingRequests}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </GlassCard>
            
            <GlassCard className="p-5 hover:shadow-glow hover:scale-[1.02] animate-fade-in">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg p-3 shadow-lg">
                  <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-600 dark:text-gray-300 truncate">Upcoming Leaves</dt>
                    <dd>
                      <div className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">{stats.upcomingLeaves}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </GlassCard>
            
            <GlassCard className="p-5 hover:shadow-glow hover:scale-[1.02] animate-fade-in">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-lg p-3 shadow-lg">
                  <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-600 dark:text-gray-300 truncate">Team On Leave Today</dt>
                    <dd>
                      <div className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">{stats.teamOnLeaveToday}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </GlassCard>
          </div>
          
          {/* Calendar and Recent Leaves */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <GlassCard className="p-5">
                <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">Team Calendar</h2>
                <div className="calendar-container">
                  <FullCalendar
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: 'dayGridMonth,dayGridWeek'
                    }}
                    events={[...events.filter(Boolean), ...bankHolidayEvents]}
                    eventClick={handleEventClick}
                    selectable={true}
                    select={handleDateSelect}
                    height="auto"
                  />
                </div>
              </GlassCard>
            </div>
            
            <div>
              <GlassCard className="p-5">
                <h2 className="text-xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-4">Recent Leave Requests</h2>
                {recentLeaves.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400">No recent leave requests</p>
                ) : (
                  <div className="space-y-4">
                    {recentLeaves.map(leave => (
                      <div key={leave.id} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0 last:pb-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {leave.user?.firstName} {leave.user?.lastName}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(leave.startDate)} - {formatDate(leave.endDate)}
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeClass(leave.status)}`}>
                            {leave.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          <span className="font-medium">{leave.typeName || (leave.type ? (leave.type.charAt(0).toUpperCase() + leave.type.slice(1)) : 'Custom')}</span>
                          {leave.notes && `: ${leave.notes}`}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </GlassCard>
            </div>
          </div>
        </>
      )}
      
      {/* Leave Request Modal */}
      {showRequestModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      New Leave Request
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedDates.start && selectedDates.end ? (
                          <>
                            From <span className="font-semibold">{formatDate(selectedDates.start)}</span> to <span className="font-semibold">{formatDate(selectedDates.end)}</span>
                          </>
                        ) : (
                          'Please select dates on the calendar'
                        )}
                      </p>
                    </div>
                    
                    <form onSubmit={handleLeaveRequestSubmit} className="mt-4">
                      <div className="mb-4">
                        <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Leave Type
                        </label>
                        <select
                          id="type"
                          name="type"
                          value={leaveRequest.type}
                          onChange={handleLeaveRequestChange}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          required
                        >
                          {availableLeaveTypes.length === 0 ? (
                            <option value="">Loading leave types...</option>
                          ) : (
                            availableLeaveTypes.map(leaveType => (
                              <option key={leaveType.id} value={leaveType.id}>
                                {leaveType.name}
                              </option>
                            ))
                          )}
                        </select>
                      </div>
                      <div className="mb-4">
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Notes (Optional)
                        </label>
                        <textarea
                          id="notes"
                          name="notes"
                          rows="3"
                          value={leaveRequest.notes}
                          onChange={handleLeaveRequestChange}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        ></textarea>
                      </div>
                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            'Submit Request'
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowRequestModal(false)}
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feature Blocked Modal */}
      <FeatureBlockedModal
        isOpen={showFeatureBlockedModal}
        onClose={() => setShowFeatureBlockedModal(false)}
        onRenewClick={handleRenewSubscription}
        daysRemaining={user?.daysRemaining || 0}
      />

      {/* Onboarding Welcome Popup */}
      <OnboardingWelcome 
        isOpen={showOnboarding}
        onClose={handleOnboardingClose}
      />
    </div>
  );
};

export default Dashboard;
